import { useState } from 'react';
import { Select } from './Select.js';
import {
  THEME_CHOICES,
  THEME_LABELS,
  readTheme,
  setTheme,
  type ThemeChoice,
} from '../lib/theme.js';

/**
 * System / Light / Dark, in the portal's header (account#87).
 *
 * ── IN THE HEADER, NOT IN PROFILE ────────────────────────────────────
 *
 * Profile is where account settings live and this looks like one, which is the
 * argument for putting it there. It loses to two things. The control has to be
 * reachable from the SIGNED-OUT screens — somebody who chose dark still sees
 * "You are signed out" and the server-fault page, and those render before there
 * is any account to hold a preference. And it is a property of this browser
 * rather than of the account: it is kept in `localStorage`, it does not follow
 * you to another device, and filing it under Profile beside your name and
 * avatar would imply it does.
 *
 * ── A DROPDOWN, NOT THREE BUTTONS OR A SWITCH ────────────────────────
 *
 * Three states need three affordances. A switch cannot express the third state
 * at all, which is the whole reason `system` exists — see `lib/theme.ts`. It is
 * the portal's own `Select` rather than a native `<select>`, whose open list the
 * browser drew light on the dark theme (account#91).
 *
 * ── LOCAL STATE IS CORRECT HERE, UNUSUALLY ───────────────────────────
 *
 * Everything else in this portal re-reads the session after a mutation rather
 * than trusting a local copy, because the server is the source. This is the
 * opposite case: the source IS this browser, `setTheme` writes it and stamps
 * the attribute synchronously, and there is nothing to go and ask.
 */
export function ThemeControl() {
  const [choice, setChoice] = useState<ThemeChoice>(readTheme);

  return (
    <Select
      size="sm"
      align="end"
      // Named for a screen reader, not visibly: the header is tight, and the
      // three option names say what this is to a sighted reader.
      label="Colour theme"
      value={choice}
      options={THEME_CHOICES.map((c) => ({ value: c, label: THEME_LABELS[c] }))}
      onChange={(next) => {
        setChoice(next);
        setTheme(next);
      }}
    />
  );
}
